import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ValidateResult, ValidatorField } from '../api/client'
import { ValidatorXmlPanel } from './ValidatorXmlPanel'
import { ResizableSplitPanel } from './ResizableSplitPanel'
import { GROUP_LABELS, groupFields, formatFileSize } from './validatorHelpers'

interface Props {
  result: ValidateResult
  filename: string | null
  fileSize?: number | null
  pdfBlobUrl: string | null
  showPdf: boolean
  onTogglePdf: () => void
  selectedLine: number | null
  onSelectLine: (line: number | null) => void
}

export function ValidatorResultDetail({
  result,
  filename,
  fileSize,
  pdfBlobUrl,
  showPdf,
  onTogglePdf,
  selectedLine,
  onSelectLine,
}: Props) {
  const { t } = useTranslation()
  type XmlLayout = 'below' | 'right'
  const [xmlLayout, setXmlLayout] = useState<XmlLayout>(() =>
    (localStorage.getItem('validator-xml-layout') as XmlLayout | null) ?? 'below'
  )

  const errorCount          = result.issues.filter(i => i.level === 'ERROR').length
  const warningCount        = result.issues.filter(i => i.level === 'WARNING').length
  const codelistWarningCount = result.issues.filter(i => i.level === 'CODELIST_WARNING').length
  const grouped: Map<string, ValidatorField[]> = groupFields(result.fields)

  const mainContent = (
    <div className="space-y-4 min-w-0 flex-1">
      {/* PDF-viewer */}
      {pdfBlobUrl && showPdf && (
        <div className="border border-violet-200 rounded-xl overflow-hidden bg-gray-50 animate-fadeSlideIn">
          <div className="flex items-center justify-between px-4 py-2 bg-violet-50 border-b border-violet-100">
            <span className="text-xs font-medium text-violet-800">
              📄 {result.pdf_filename ?? t('validator_result_detail.pdf_attachment')}
            </span>
            <a
              href={pdfBlobUrl}
              download={result.pdf_filename ?? t('validator_result_detail.pdf_attachment_filename')}
              className="text-xs text-violet-600 hover:text-violet-800 hover:underline"
            >
              ↓ {t('validator_result_detail.download')}
            </a>
          </div>
          <iframe
            src={pdfBlobUrl}
            className="w-full"
            style={{ height: '600px' }}
            title={result.pdf_filename ?? t('validator_result_detail.pdf_attachment')}
          />
        </div>
      )}

      {/* AI-analyse */}
      {(result.ai_summary || result.ai_anomalies.length > 0) && (
        <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-100 rounded-xl px-4 py-3 space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-violet-700 uppercase tracking-wide">
            <span>✦</span> {t('validator_result_detail.ai_summary')}
          </div>
          {result.ai_summary && (
            <p className="text-sm text-gray-800">{result.ai_summary}</p>
          )}
          {result.ai_anomalies.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {result.ai_anomalies.map((a, i) => (
                <li key={i} className="text-xs text-amber-700 flex items-start gap-1.5">
                  <span className="mt-0.5 shrink-0">⚠</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Validatieproblemen */}
      {result.issues.length > 0 && (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {t('validator_result_detail.validation_issues')}
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {result.issues.map((issue, i) => (
              <button
                key={i}
                onClick={() => onSelectLine(issue.line)}
                className={`w-full text-left px-4 py-2.5 flex items-start gap-3 text-xs transition-colors hover:bg-gray-50 ${
                  selectedLine === issue.line ? 'bg-yellow-50' : ''
                }`}
              >
                <span className={`shrink-0 mt-0.5 font-semibold ${
                  issue.level === 'ERROR'
                    ? 'text-red-600'
                    : issue.level === 'CODELIST_WARNING'
                    ? 'text-orange-600'
                    : 'text-amber-600'
                }`}>
                  {issue.level === 'ERROR' ? '✗' : issue.level === 'CODELIST_WARNING' ? '◈' : '⚠'} {t('validator_result_detail.line')} {issue.line}
                </span>
                {issue.label && (
                  <span className="shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
                    {issue.label}
                  </span>
                )}
                <span className="text-gray-700">{issue.message}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Veldentabel */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            {t('validator_result_detail.document_content')} — {result.fields.length} {t('validator_result_detail.fields')}
          </span>
        </div>
        {result.fields.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-gray-400">{t('validator_result_detail.no_fields_available')}</p>
            <p className="text-xs text-gray-400 mt-1">
              {t('validator_result_detail.field_extraction_supported')}
            </p>
          </div>
        ) : (
          <div>
            {Array.from(grouped.entries()).map(([group, fields], gi) => (
              <div key={group}>
                <div className={`px-4 py-1.5 bg-gray-50 border-b border-gray-100 ${gi > 0 ? 'border-t border-gray-200' : ''}`}>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {GROUP_LABELS[group] ?? group}
                  </span>
                </div>
                {fields.map((f, i) => (
                  <button
                    key={i}
                    onClick={() => onSelectLine(f.line)}
                    disabled={!f.line}
                    className={`w-full text-left px-4 py-2 flex items-center gap-4 text-sm border-b border-gray-50 last:border-0 transition-colors ${
                      f.line
                        ? selectedLine === f.line
                          ? 'bg-yellow-50 hover:bg-yellow-100 cursor-pointer'
                          : 'hover:bg-gray-50 cursor-pointer'
                        : 'cursor-default'
                    }`}
                  >
                    {group === 'regels' ? (
                      <>
                        <span className="text-gray-700 flex-1 min-w-0 truncate text-xs font-medium" title={f.label ?? undefined}>{f.label}</span>
                        <span className="text-gray-500 shrink-0 text-xs font-mono text-right">{f.value}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-gray-500 shrink-0 w-48 text-xs">{f.label}</span>
                        <span className="text-gray-900 font-medium flex-1 text-xs font-mono">{f.value}</span>
                      </>
                    )}
                    {f.line ? (
                      <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-mono transition-colors ${
                        selectedLine === f.line
                          ? 'bg-yellow-200 text-yellow-800'
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}>
                        {f.line}
                      </span>
                    ) : (
                      <span className="shrink-0 w-12" />
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* XML-viewer — onder (alleen in 'below'-modus) */}
      {xmlLayout === 'below' && selectedLine !== null && (
        <ValidatorXmlPanel
          xmlContent={result.xml_content}
          originalXmlContent={result.original_xml_content}
          targetLine={selectedLine}
          onClose={() => onSelectLine(null)}
        />
      )}
    </div>
  )

  return (
    <div className="space-y-4 animate-fadeSlideIn min-w-0">

      {/* Koptegel: standaard + validatiestatus + layout-toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {filename && (
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-xs" title={filename}>
              {filename}
            </span>
          )}
          {fileSize != null && (
            <span className="text-xs text-gray-400">{formatFileSize(fileSize)}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-semibold rounded-full">
            {result.standard}
          </span>
          <span className="px-2.5 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-full">
            {result.document_type}
          </span>
          {result.is_valid && errorCount === 0 ? (
            <span className="flex items-center gap-1 px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold rounded-full">
              ✓ {t('validator_result_detail.valid')}
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2.5 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold rounded-full">
              ✗ {t('validator_result_detail.error_count', { count: errorCount })}
            </span>
          )}
          {warningCount > 0 && (
            <span className="flex items-center gap-1 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-xs font-semibold rounded-full">
              ⚠ {t('validator_result_detail.warning_count', { count: warningCount })}
            </span>
          )}
          {codelistWarningCount > 0 && (
            <span className="flex items-center gap-1 px-2.5 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
              ◈ {t('validator_result_detail.codelist_warning_count', { count: codelistWarningCount })}
            </span>
          )}
          {!result.xsd_available && (
            <span className="text-xs text-gray-400 italic">{t('validator_result_detail.xsd_not_available')}</span>
          )}
          {pdfBlobUrl && (
            <button
              onClick={onTogglePdf}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                showPdf
                  ? 'bg-violet-100 border-violet-200 text-violet-800'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-violet-300 hover:text-violet-700'
              }`}
            >
              <span>📄</span>
              {result.pdf_filename ?? t('validator_result_detail.attachment')}
              <span className="text-[10px]">{showPdf ? '▲' : '▼'}</span>
            </button>
          )}

          {/* XML-layout toggle */}
          <div className="flex items-center rounded-full border border-gray-200 overflow-hidden text-xs">
            <button
              onClick={() => { setXmlLayout('below'); localStorage.setItem('validator-xml-layout', 'below') }}
              title={t('validator_result_detail.xml_viewer_below')}
              className={`flex items-center gap-1 px-2.5 py-1 transition-colors ${
                xmlLayout === 'below'
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                <rect x="1" y="2" width="14" height="5" rx="1"/>
                <rect x="1" y="9" width="14" height="5" rx="1"/>
              </svg>
              {t('validator_result_detail.below')}
            </button>
            <button
              onClick={() => { setXmlLayout('right'); localStorage.setItem('validator-xml-layout', 'right') }}
              title={t('validator_result_detail.xml_viewer_right')}
              className={`flex items-center gap-1 px-2.5 py-1 transition-colors border-l border-gray-200 ${
                xmlLayout === 'right'
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                <rect x="1" y="2" width="6" height="12" rx="1"/>
                <rect x="9" y="2" width="6" height="12" rx="1"/>
              </svg>
              {t('validator_result_detail.right')}
            </button>
          </div>
        </div>
      </div>

      {/* Content + XML-viewer (layout afhankelijk) */}
      {xmlLayout === 'right' && selectedLine !== null ? (
        <ResizableSplitPanel
          defaultLeftPercent={55}
          left={mainContent}
          right={
            <ValidatorXmlPanel
              xmlContent={result.xml_content}
              originalXmlContent={result.original_xml_content}
              targetLine={selectedLine}
              onClose={() => onSelectLine(null)}
              className="border border-gray-200 rounded-xl bg-white overflow-hidden animate-fadeSlideIn h-full"
              fillHeight
            />
          }
        />
      ) : (
        mainContent
      )}
    </div>
  )
}
